import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Users, DollarSign, BarChart3, Activity,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Search,
} from 'lucide-react';
import { useThemeStore } from '../../stores/theme.store';
import { usePerCustomerReport, useExportReport } from '../../hooks/useFinancialReports';
import type { PerCustomerReportFilters } from '../../hooks/useFinancialReports';

function fmtBRL(centavos: number | undefined | null): string {
  if (centavos === null || centavos === undefined) return 'R$ 0,00';
  return `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getDefaultDates() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0],
  };
}

type SortField = 'volume' | 'revenue' | 'balance' | 'name' | 'transactions';

function StatusBadge({ status, type }: { status: string; type: 'contract' | 'kyc' }) {
  let color = 'bg-gray-100 text-gray-700';
  if (type === 'contract') {
    if (status === 'ACTIVE') color = 'bg-green-100 text-green-700';
    else if (status === 'BLOCKED') color = 'bg-red-100 text-red-700';
    else if (status === 'SUSPENDED') color = 'bg-yellow-100 text-yellow-700';
  } else {
    if (status === 'APPROVED') color = 'bg-green-100 text-green-700';
    else if (status === 'PENDING') color = 'bg-yellow-100 text-yellow-700';
    else if (status === 'REJECTED') color = 'bg-red-100 text-red-700';
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {status || 'N/A'}
    </span>
  );
}

export function PerCustomerReportPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useThemeStore();
  const defaults = getDefaultDates();

  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('volume');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMutation = useExportReport();

  const filters: PerCustomerReportFilters = useMemo(() => ({
    startDate,
    endDate,
    ...(status ? { status } : {}),
    sortBy,
    sortOrder,
    ...(search ? { search } : {}),
    page,
    limit,
  }), [startDate, endDate, status, sortBy, sortOrder, search, page, limit]);

  const { data, isLoading } = usePerCustomerReport(filters);

  const bg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';

  const handleExport = (format: 'PDF' | 'CSV' | 'XLSX') => {
    setShowExportMenu(false);
    exportMutation.mutate({ reportType: 'per-customer', format, filters });
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortOrder === 'desc'
      ? <ArrowDown className="w-3 h-3" />
      : <ArrowUp className="w-3 h-3" />;
  };

  const summary = data?.summary;
  const customers = data?.customers ?? [];
  const pagination = data?.pagination;

  // Skeleton loader
  if (isLoading) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-8 w-64 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`${cardBg} rounded-xl p-5 space-y-3`}>
                <div className="h-4 w-24 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
                <div className="h-7 w-36 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
              </div>
            ))}
          </div>
          <div className={`${cardBg} rounded-xl p-6 h-96 animate-pulse`} />
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || customers.length === 0) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate('/admin/reports')} className={`flex items-center gap-2 mb-6 ${textSecondary} hover:opacity-80 transition-opacity`}>
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className={`${cardBg} rounded-xl p-12 text-center`}>
            <Users className={`w-12 h-12 mx-auto mb-4 ${textSecondary}`} />
            <h2 className={`text-lg font-semibold ${textPrimary}`}>Nenhum correntista encontrado</h2>
            <p className={`mt-2 ${textSecondary}`}>Não há dados de correntistas para os filtros selecionados. Ajuste os filtros e tente novamente.</p>
          </div>
        </div>
      </div>
    );
  }

  const summaryCards = [
    { label: 'Total de Correntistas', value: String(summary?.totalActiveCustomers ?? 0), color: 'text-blue-500', icon: Users, iconBg: 'bg-blue-500/10' },
    { label: 'Volume Total Movimentado', value: fmtBRL(summary?.totalVolume), color: 'text-green-500', icon: BarChart3, iconBg: 'bg-green-500/10' },
    { label: 'Total Taxas Arrecadadas', value: fmtBRL(summary?.totalFeesCollected), color: 'text-purple-500', icon: DollarSign, iconBg: 'bg-purple-500/10' },
    { label: 'Média Transações/Correntista', value: (summary?.avgTransactionsPerCustomer ?? 0).toFixed(1), color: 'text-orange-500', icon: Activity, iconBg: 'bg-orange-500/10' },
  ];

  return (
    <div className={`min-h-screen ${bg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/reports')} className={`p-2 rounded-lg ${cardBg} border ${border} hover:opacity-80 transition-opacity`}>
              <ArrowLeft className={`w-4 h-4 ${textPrimary}`} />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${textPrimary}`}>Relatório por Correntista</h1>
              <p className={`text-sm ${textSecondary}`}>Detalhamento individual de atividade por conta</p>
            </div>
          </div>
          {/* Export button */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </button>
            {showExportMenu && (
              <div className={`absolute right-0 mt-2 w-40 rounded-lg shadow-lg border ${border} ${cardBg} z-10`}>
                {(['PDF', 'CSV', 'XLSX'] as const).map((fmt) => (
                  <button key={fmt} onClick={() => handleExport(fmt)} className={`block w-full text-left px-4 py-2 text-sm ${textPrimary} hover:bg-blue-500/10 first:rounded-t-lg last:rounded-b-lg`}>
                    {fmt === 'XLSX' ? 'Excel' : fmt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className={`${cardBg} rounded-xl p-4 border ${border}`}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Data Início</label>
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Data Fim</label>
              <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Status da Conta</label>
              <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}>
                <option value="">Todos</option>
                <option value="ACTIVE">Ativa</option>
                <option value="BLOCKED">Bloqueada</option>
                <option value="SUSPENDED">Suspensa</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Ordenar por</label>
              <select value={sortBy} onChange={(e) => { setSortBy(e.target.value as SortField); setPage(1); }} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}>
                <option value="volume">Volume Movimentado</option>
                <option value="revenue">Taxas Pagas</option>
                <option value="balance">Saldo Atual</option>
                <option value="name">Nome</option>
                <option value="transactions">Qtd Transações</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Buscar por nome ou documento</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Nome ou CPF/CNPJ..."
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm ${inputBg}`}
                />
                <button onClick={handleSearch} className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className={`${cardBg} rounded-xl p-5 border ${border}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${card.iconBg}`}>
                    <Icon className={`w-5 h-5 ${card.color}`} />
                  </div>
                  <span className={`text-sm font-medium ${textSecondary}`}>{card.label}</span>
                </div>
                <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
              </div>
            );
          })}
        </div>

        {/* Table */}
        <div className={`${cardBg} rounded-xl border ${border} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${border} ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>#</th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary} cursor-pointer select-none`} onClick={() => handleSort('name')}>
                    <span className="flex items-center gap-1">Nome <SortIcon field="name" /></span>
                  </th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>CPF/CNPJ</th>
                  <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Conta Virtual</th>
                  <th className={`px-4 py-3 text-right font-medium ${textSecondary} cursor-pointer select-none`} onClick={() => handleSort('volume')}>
                    <span className="flex items-center justify-end gap-1">Volume Mov. <SortIcon field="volume" /></span>
                  </th>
                  <th className={`px-4 py-3 text-right font-medium ${textSecondary} cursor-pointer select-none`} onClick={() => handleSort('balance')}>
                    <span className="flex items-center justify-end gap-1">Saldo Atual <SortIcon field="balance" /></span>
                  </th>
                  <th className={`px-4 py-3 text-right font-medium ${textSecondary} cursor-pointer select-none`} onClick={() => handleSort('revenue')}>
                    <span className="flex items-center justify-end gap-1">Taxas Pagas <SortIcon field="revenue" /></span>
                  </th>
                  <th className={`px-4 py-3 text-right font-medium ${textSecondary} cursor-pointer select-none`} onClick={() => handleSort('transactions')}>
                    <span className="flex items-center justify-end gap-1">Transações <SortIcon field="transactions" /></span>
                  </th>
                  <th className={`px-4 py-3 text-center font-medium ${textSecondary}`}>Contrato</th>
                  <th className={`px-4 py-3 text-center font-medium ${textSecondary}`}>KYC</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.virtualAccountId} className={`border-b ${border} hover:${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} transition-colors`}>
                    <td className={`px-4 py-3 font-medium ${textSecondary}`}>{c.ranking}</td>
                    <td className={`px-4 py-3 font-medium ${textPrimary}`}>{c.userName}</td>
                    <td className={`px-4 py-3 font-mono text-xs ${textSecondary}`}>{c.userDocument}</td>
                    <td className={`px-4 py-3 font-mono text-xs ${textSecondary}`}>{c.virtualAccountId.slice(0, 8)}...</td>
                    <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL((c.inflow ?? 0) + (c.outflow ?? 0))}</td>
                    <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL((c.availableBalance ?? 0) + (c.blockedBalance ?? 0))}</td>
                    <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmtBRL((c.cashInFeesPaid ?? 0) + (c.membershipFeesPaid ?? 0))}</td>
                    <td className={`px-4 py-3 text-right ${textPrimary}`}>{c.transactionCount}</td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={c.contractStatus} type="contract" /></td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={c.kycOriginStatus} type="kyc" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && (
            <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t ${border}`}>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${textSecondary}`}>Itens por página:</span>
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className={`rounded-lg border px-2 py-1 text-sm ${inputBg}`}
                >
                  {[10, 25, 50, 100].map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                <span className={`text-sm ${textSecondary}`}>
                  {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className={`p-2 rounded-lg border ${border} ${cardBg} disabled:opacity-40 hover:opacity-80 transition-opacity`}
                >
                  <ChevronLeft className={`w-4 h-4 ${textPrimary}`} />
                </button>
                <span className={`text-sm ${textPrimary}`}>
                  Página {pagination.page} de {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={page >= pagination.pages}
                  className={`p-2 rounded-lg border ${border} ${cardBg} disabled:opacity-40 hover:opacity-80 transition-opacity`}
                >
                  <ChevronRight className={`w-4 h-4 ${textPrimary}`} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
